<template>
  <div>
    <div class="card my-3">
      <div class="card-header">
        <a href v-on:click.prevent="deleteGroup()" class="text-danger float-right">
          <i class="fa fa-trash-o" aria-hidden="true"></i>
        </a> {{ $t('Conditions Group') }}
      </div>
      <div class="card-body">
        <p class="card-text">
          <small class="text-muted">
            <strong>{{ $t('Contacts must match all of the following conditions') }}</strong>
          </small>
        </p>

        <div v-for="(criteria, index) in group" :key="index">
          <SegmentCriteria
            :criteria="criteria"
            :availableSegments="segments"
            v-on:delete-criteria="deleteCriteria(index)"
            v-on:edit-criteria="editCriteria(index, criteria)"
            :activities="activities"
            :state="state">
          </SegmentCriteria>
        </div>
        <div class="my-2">
          <button class="d-inline-block btn btn-sm btn-outline-secondary rounded-pill" type="button" v-on:click="modalIsOpen = true">
            <i aria-hidden="true" class="fa fa-plus"></i> {{ $t('Add filter') }}
          </button>
        </div>
      </div>
    </div>

    <Dialog :open="modalIsOpen" @update:open="onDialogUpdate">
      <DialogScrollContent>
        <form class="tw:min-w-0">
          <DialogHeader>
            <DialogTitle>
              {{ $t('Add condition') }}
            </DialogTitle>
          </DialogHeader>

          <div class="form-group">
            <Combobox
              v-model="criteriaType"
              :options="segments.map(segment => ({
                value: segment.type,
                label: segment.name
              }))"
              :fullwidth="true"
            />
          </div>
          <div v-if="criteriaType && criteriaType === 'variants'">
            <ActivitiesAndVariantsSelector
              :variants-selected="Array.isArray(criteriaValue) ? criteriaValue : []"
              :activitiesSelectList="activities"
              @change-variants-list="criteriaValue = $event"
            ></ActivitiesAndVariantsSelector>
          </div>
          <div v-else-if="criteriaType && criteriaType === 'extrafields'" class="form-group">
            <Extrafields :options="segmentOptions" v-model="criteriaValue"></Extrafields>
          </div>
          <div v-else-if="criteriaType" class="form-group">
            <Combobox
              v-model="criteriaValue"
              :options="segmentOptions.map(option => ({
                value: option.id,
                label: option.name,
                disabled: option.disabled
              }))"
              :fullwidth="true"
              :virtualize="false"
            />
          </div>

          <DialogFooter>
            <button type="button" name="button" class="btn btn-primary" :disabled="disableAdd" @click="onClickAdd">
              {{ $t('Add') }}
            </button>
          </DialogFooter>
        </form>
      </DialogScrollContent>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import Extrafields from "@/components/marketing-message/registration-segment/criteria-types/Extrafields.vue";
import SegmentCriteria from "@/components/marketing-message/registration-segment/SegmentCriteria.vue";
import ActivitiesAndVariantsSelector from "@/components/ActivitiesAndVariantsSelector.vue";
import { Dialog, DialogScrollContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Combobox from "@/components/ui/Combobox.vue";

const props = defineProps({
  group: {
    type: Array,
    default: () => []
  },
  segments: {
    type: Array,
    default: () => []
  },
  activitiesUrl: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update-group', 'delete-group']);

const criteriaIndex = ref(null);
const criteriaType = ref(null);
const criteriaValue = ref(null);
const activities = ref([]);
const state = ref('');
const modalIsOpen = ref(false);

const disableAdd = computed(() => !criteriaValue.value);

const segmentOptions = computed(() => {
  return props.segments.find(segment => segment.type === criteriaType.value)?.options ?? [];
});

const groupCriteria = ref(props.group.length > 0 ? props.group : []);
watch(() => props.group, (newGroup) => {
  groupCriteria.value = newGroup;
}, { deep: true });

const onDialogUpdate = (isOpen) => {
  modalIsOpen.value = isOpen;

  if (!isOpen) {
    resetCriteria();
  }
};

const resetCriteria = () => {
  criteriaIndex.value = null;
  criteriaType.value = '';
  criteriaValue.value = null;
};

const onClickAdd = () => {
  addCriteria();
};

const addCriteria = () => {
  if (criteriaIndex.value !== null) {
    groupCriteria.value.splice(criteriaIndex.value, 1, {
      type: criteriaType.value,
      value: criteriaValue.value,
    });
  } else {
    groupCriteria.value.push({
      type: criteriaType.value,
      value: criteriaValue.value,
    });
  }
  emit('update-group', groupCriteria.value);
  resetCriteria();
  modalIsOpen.value = false;
};

const deleteCriteria = (index) => {
  groupCriteria.value.splice(index, 1);
};

const editCriteria = (index, criteria) => {
  criteriaIndex.value = index;
  criteriaType.value = criteria.type;
  criteriaValue.value = criteria.value;
  modalIsOpen.value = true;
};

const deleteGroup = () => {
  emit('delete-group');
};

const getCatalog = async () => {
  try {
    state.value = 'initializing';
    const { data } = await axios.get(props.activitiesUrl);
    state.value = 'idle';
    activities.value = data.data;
    activities.value = activities.value.reduce(
      function (accu, activity) {
        accu.push(activity);
        activity.associated_activities.forEach(
          (associated_activity) => accu.push(associated_activity));
        return accu;
      }, []);
  } catch (e) {
    alert(e.message);
  }
};

onMounted(() => {
  /** this is a fix to avoid ajax error if activity is not available */
  if (props.segments.some((segment) => segment.type === 'variants')) {
    getCatalog();
  }
});
</script>
